import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  DEEP_LINK_FOCUS_FLASH_MS,
  useDeepLinkFocus,
} from "@/components/ui/deep-link-focus/useDeepLinkFocus";
import { useOsActionsOptional, useWindowById } from "@/components/ui/os-layout/OsContext";
import { useWindowId } from "@/components/ui/os-layout/OsWindow";
import { entityIdFromFocusKey, readAgentFocusParams } from "@/services/agent/agentNavigation";

type UseAgentListFocusOptions<T> = {
  /** Focus key prefix, e.g. `case_check` for `case_check:<id>`. */
  focusPrefix: string;
  rows: readonly T[];
  getRowId: (row: T) => string;
  /** Optional search priming when the focused row is not yet in the loaded list. */
  primeSearchForId?: (id: string) => void | Promise<void>;
  enabled?: boolean;
};

/**
 * Standard list-focus hook for agent-navigated apps.
 * Reads focus from OS window params or URL; repeat flashes use window focusNonce.
 */
export function useAgentListFocus<T>({
  focusPrefix,
  rows,
  getRowId,
  primeSearchForId,
  enabled = true,
}: UseAgentListFocusOptions<T>): {
  focusKey: string | null;
  focusedId: string | null;
} {
  const windowId = useWindowId();
  const osWindow = useWindowById(windowId ?? "");
  const osActions = useOsActionsOptional();
  const [searchParams] = useSearchParams();
  const { focusKey, focusSeq } = readAgentFocusParams(osWindow, searchParams);
  const focusedId = entityIdFromFocusKey(focusKey, focusPrefix);
  const primedForIdRef = useRef<string | null>(null);
  const consumedFocusTokenRef = useRef<string | null>(null);

  useDeepLinkFocus(focusKey, { focusSeq, enabled: enabled && Boolean(focusKey) });

  useEffect(() => {
    if (!enabled || !focusKey || !windowId || !osWindow || !osWindow.params.focus) {
      return;
    }
    const token = `${focusKey}\0${focusSeq ?? ""}`;
    if (consumedFocusTokenRef.current === token) {
      return;
    }
    const timer = window.setTimeout(() => {
      consumedFocusTokenRef.current = token;
      if (!osWindow.params.focus) {
        return;
      }
      const nextParams = { ...osWindow.params };
      delete nextParams.focus;
      osActions?.updateWindow(windowId, { params: nextParams });
    }, DEEP_LINK_FOCUS_FLASH_MS + 120);
    return () => window.clearTimeout(timer);
  }, [enabled, focusKey, focusSeq, osActions, osWindow, windowId]);

  useEffect(() => {
    if (!enabled || !focusedId || !primeSearchForId) {
      return;
    }
    if (primedForIdRef.current === focusedId) {
      return;
    }
    const inList = rows.some((row) => getRowId(row) === focusedId);
    primedForIdRef.current = focusedId;
    if (inList) {
      return;
    }
    void primeSearchForId(focusedId);
  }, [enabled, focusedId, rows, getRowId, primeSearchForId]);

  return { focusKey, focusedId };
}
